import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  SignUpPage,
  WelcomePage,
} from '../features/auth/account-pages.js';
import { InvitePage } from '../features/auth/invite-page.js';
import { CatchUpPage } from '../features/catch-up/catch-up-page.js';
import { ConnectionsPage } from '../features/connections/connections-page.js';
import { ImportPage } from '../features/connections/import-page.js';
import { StatusPage } from '../features/status/status-page.js';
import { InvoiceEditorPage } from '../features/invoicing/invoice-editor-page.js';
import { InvoicePrintPage } from '../features/invoicing/invoice-print-page.js';
import { InvoicesPage } from '../features/invoicing/invoices-page.js';
import { CapturePage } from '../features/capture/capture-page.js';
import { AnswerPage } from '../features/questions/answer-page.js';
import { QuestionsPage } from '../features/questions/questions-page.js';
import { PayrollPage } from '../features/payroll/payroll-page.js';
import { VehiclesPage } from '../features/vehicles/vehicles-page.js';
import { LoginPage } from '../features/auth/login-page.js';
import { DocumentsPage } from '../features/documents/documents-page.js';
import { BookkeepingPage } from '../features/bookkeeping/bookkeeping-page.js';
import { BookkeepingRecordPage } from '../features/bookkeeping/bookkeeping-record-page.js';
import { VendorMemoryPage } from '../features/intelligence/vendor-memory-page.js';
import { VendorsPage } from '../features/vendors/vendors-page.js';
import { VendorDetailPage } from '../features/vendors/vendor-detail-page.js';
import { TaxPage } from '../features/tax/tax-page.js';
import { BankPage } from '../features/bank/bank-page.js';
import { ExportPage } from '../features/export/export-page.js';
import { ReviewQueuePage } from '../features/review/review-queue-page.js';
import { ReviewPage } from '../features/review/review-page.js';
import { DocumentDetailPage } from '../features/documents/document-detail-page.js';
import { AppLayout } from '../features/shell/app-layout.js';
import { PulsePage } from '../features/pulse/pulse-page.js';
import { ClosePage } from '../features/close/close-page.js';
import { SettingsPage } from '../features/settings/settings-page.js';
import { ReportsPage } from '../features/reports/reports-page.js';
import { BooksPage } from '../features/books/books-page.js';
import { NewOrganizationPage } from '../features/organizations/new-organization-page.js';
import { CustomerDetailPage } from '../features/parties/customer-detail-page.js';
import { EquipmentDetailPage } from '../features/parties/equipment-detail-page.js';
import { EquipmentPage } from '../features/parties/equipment-page.js';
import { CustomersPage } from '../features/parties/customers-page.js';
import { JobDetailPage } from '../features/parties/job-detail-page.js';
import { JobsPage } from '../features/parties/jobs-page.js';
import { MissionControlPage } from '../features/firm/mission-control-page.js';
import { EverythingPage } from '../features/everything/everything-page.js';
import { TimelinePage } from '../features/timeline/timeline-page.js';
import { NotFoundPage } from '../features/shell/not-found-page.js';
import { RouteErrorPage } from '../features/shell/route-error-page.js';
import { RootLayout } from './root-layout.js';
import type { AuthContextValue } from '../shared/auth/auth-context.js';
import { sessionQuery } from '../shared/api/queries.js';

/**
 * Route tree.
 *
 * Defined in code rather than generated from the filesystem. The tree is small
 * enough to read at a glance, there is no generated artifact to keep in sync,
 * and, most importantly, the authentication guard is visible right next to
 * the routes it protects instead of implied by a directory name.
 */

export interface RouterContext {
  readonly auth: AuthContextValue;
  readonly queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorPage,
});

/** Where to return the user after they sign in. */
const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

/**
 * An invitation link. Public: the page decides between sign up, sign in and
 * accept from who is here, and the server checks the address either way.
 */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
});

/** A question's link, opened by the client. Public: the token is the credential. */
const answerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/q/$token',
  component: AnswerPage,
});

/**
 * The invoice alone, for printing. Outside the app chrome but behind the
 * same guard as everything in it.
 */
const invoicePrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$invoiceId/print',
  beforeLoad: ({ context, location }) => {
    if (context.auth.status !== 'authenticated') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(sessionQuery()),
  component: InvoicePrintPage,
});

/** Opening an account without an invitation. */
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'authenticated') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/' });
    }
  },
  component: SignUpPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/** Where an emailed reset link lands, signed in by the link itself. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

/** Public: whether the service is working, for anyone. */
const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/status',
  component: StatusPage,
});

/** Where a sign-up confirmation link lands. */
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearchSchema,
  beforeLoad: ({ context }) => {
    // An authenticated user has no business on the sign-in screen.
    if (context.auth.status === 'authenticated') {
      // TanStack Router signals a redirect by throwing a plain descriptor
      // rather than an Error; this is the library's documented control flow.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

/**
 * The authenticated area.
 *
 * A pathless layout route: it contributes the guard and the chrome without
 * adding a URL segment. Every route nested beneath it inherits the check, so a
 * new page cannot be added unprotected by accident.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: ({ context, location }) => {
    if (context.auth.status !== 'authenticated') {
      // See the note on the login route: redirect descriptors are thrown, and
      // they are deliberately not Error instances.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  loader: ({ context }) =>
    // Fetched before the layout renders so the shell never flashes empty and
    // every child route can read the session from the cache synchronously.
    context.queryClient.ensureQueryData(sessionQuery()),
  component: AppLayout,
});

/** `?ask=` lets ⌘K hand a typed question straight to the Ask engine. */
const pulseSearchSchema = z.object({
  ask: z.string().optional(),
});

const overviewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  validateSearch: pulseSearchSchema,
  component: PulsePage,
});

const newOrganizationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/organizations/new',
  component: NewOrganizationPage,
});

const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers',
  component: CustomersPage,
});

const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/$customerId',
  component: CustomerDetailPage,
});

const jobsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/jobs',
  component: JobsPage,
});

const jobDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/jobs/$jobId',
  component: JobDetailPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: MissionControlPage,
});

const everythingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/everything',
  validateSearch: z.object({ q: z.string().optional() }),
  component: EverythingPage,
});

const equipmentRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/equipment',
  component: EquipmentPage,
});

const equipmentDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/equipment/$equipmentId',
  component: EquipmentDetailPage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices',
  component: InvoicesPage,
});

const newInvoiceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices/new',
  component: InvoiceEditorPage,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices/$invoiceId',
  component: InvoiceEditorPage,
});

const questionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/questions',
  component: QuestionsPage,
});

const catchUpRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/catch-up',
  component: CatchUpPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/connections',
  // Where a sign-in with a CRM comes back to, saying how it went.
  validateSearch: z.object({
    connected: z.string().optional(),
    error: z.string().optional(),
    organization: z.string().optional(),
    provider: z.string().optional(),
  }),
  component: ConnectionsPage,
});

const importRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/connections/import',
  component: ImportPage,
});

const vehiclesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/vehicles',
  component: VehiclesPage,
});

const payrollRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payroll',
  component: PayrollPage,
});

const captureRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/capture',
  component: CapturePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
});

const closeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/close',
  component: ClosePage,
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: ReportsPage,
});

const booksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/books',
  component: BooksPage,
});

const timelineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/timeline',
  component: TimelinePage,
});

const documentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/documents',
  component: DocumentsPage,
});

const documentDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/documents/$documentId',
  component: DocumentDetailPage,
});

const reviewQueueRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/review',
  component: ReviewQueuePage,
});

const reviewItemRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/review/$itemId',
  component: ReviewPage,
});

const bookkeepingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bookkeeping',
  component: BookkeepingPage,
});

const bookkeepingRecordRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bookkeeping/$recordId',
  component: BookkeepingRecordPage,
});

const vendorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers',
  component: VendorsPage,
});

const vendorDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers/$vendorId',
  component: VendorDetailPage,
});

/** v0.4's observation view, now one level down from the supplier itself. */
const vendorMemoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers/$vendorId/memory',
  component: VendorMemoryPage,
});

const taxRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/tax',
  component: TaxPage,
});

const exportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/export',
  component: ExportPage,
});

const bankRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bank',
  component: BankPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  welcomeRoute,
  statusRoute,
  inviteRoute,
  invoicePrintRoute,
  answerRoute,
  protectedRoute.addChildren([
    overviewRoute,
    clientsRoute,
    everythingRoute,
    timelineRoute,
    newOrganizationRoute,
    invoicesRoute,
    newInvoiceRoute,
    invoiceDetailRoute,
    captureRoute,
    questionsRoute,
    payrollRoute,
    vehiclesRoute,
    catchUpRoute,
    connectionsRoute,
    importRoute,
    documentsRoute,
    documentDetailRoute,
    reviewQueueRoute,
    reviewItemRoute,
    bookkeepingRoute,
    bookkeepingRecordRoute,
    vendorsRoute,
    vendorDetailRoute,
    vendorMemoryRoute,
    customersRoute,
    customerDetailRoute,
    jobsRoute,
    jobDetailRoute,
    equipmentRoute,
    equipmentDetailRoute,
    closeRoute,
    settingsRoute,
    reportsRoute,
    booksRoute,
    taxRoute,
    exportRoute,
    bankRoute,
  ]),
]);

/**
 * Placeholder context.
 *
 * `createRouter` requires a context at construction, but the real one comes
 * from React state. A concrete anonymous value is supplied rather than a
 * non-null assertion, so a bug that fails to inject the live context degrades
 * to "signed out" instead of dereferencing undefined.
 */
const placeholderAuth: AuthContextValue = {
  status: 'loading',
  userId: null,
  email: null,
  signIn: () => Promise.reject(new Error('Auth context is not ready.')),
  signOut: () => Promise.reject(new Error('Auth context is not ready.')),
};

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { auth: placeholderAuth, queryClient },
    defaultPreload: 'intent',
    // Data is owned by TanStack Query; the router should not cache it too.
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: RouteErrorPage,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
